import {Player} from './Player';

type PlayerControlsCC={
    button: JQuery<HTMLElement>,
    tracks: JQuery<HTMLElement>
}

type PlayerControls={
    play: JQuery<HTMLElement>,
    progress: JQuery<HTMLElement>,
    mute: JQuery<HTMLElement>,
    volume: JQuery<HTMLInputElement>,
    transcript?: JQuery<HTMLElement>,
    cc?: PlayerControlsCC
};

type ProgressTexts={
    current: JQuery<HTMLElement>,
    duration: JQuery<HTMLElement>,
    remaining: JQuery<HTMLElement>
}

export class CustomPlayer extends Player {

    protected controls:PlayerControls;
    protected progressBar:JQuery<HTMLInputElement>;
    protected progressText:ProgressTexts;
    protected hasReplay;
    protected changing=false;
    protected ccLine;
    protected trackID=-1;

    constructor(protected container: JQuery, protected element: JQuery<HTMLVideoElement|HTMLAudioElement>) {
        super(element, container);
        this.element[0].controls = false;

        if (this.autoPlay=='visible') {
            this.container.on('observer.in', (e)=>this.play(e));
            this.container.on('observer.out', (e)=>this.pause(e));
        }

        if(this.autoStop) this.container.on('observer.out',(e)=>this.pause(e));

        if(this.element) this.element.on('loadedmetadata', ()=>this.loadMeta());

        this.container.find('.controls').on('pointerdown',(e)=>{
            e.stopPropagation();
        });

        this.controls = {
            play: container.find('.playpause'),
            progress: container.find('.progress'),
            mute: container.find('.mute'),
            volume: container.find('.volume'),
        };

        this.progressBar = this.controls.progress.find('input');
        this.progressText = {
            current: container.find('.progress-time .current'),
            duration: container.find('.progress-time .duration'),
            remaining: container.find('.progress-time .remaining')
        };

        this.hasReplay = this.container.hasClass('replay');
        if(this.hasReplay && this.element) this.element[0].addEventListener('ended', (ev)=>this.replay());

        this.controls.play.on('click keydown', (e)=>{
            this.togglePlay(e);
            this.textSwitch(this.controls.play);
        });

        if(this.element[0].textTracks.length > 0 ){
            this.controls.transcript = this.container.find('.description');
            this.controls.cc = {
                button: this.container.find('.subtitles'),
                tracks: this.container.find('.subtitle')
            }

            this.controls.transcript.click(()=>{
                this.toggleDescription();
            });

            let track=this.element.find('track');

            this.ccLine=this.container.find('.cc-line');

            track.on('cuechange',(e)=>this.oncuechange());

            let tracks = [...this.element[0].textTracks].filter(t => (t.kind === 'captions' || t.kind === 'subtitles'));
            tracks.forEach(track => {
                track.mode = 'hidden';
            });

            let ccButton=this.controls.cc.button;
             ccButton.click((e)=>{
                ccButton.toggleClass('open');
                this.textSwitch(ccButton);
            });

            this.controls.cc.tracks.click((e)=>{
                this.toggleCaptions(e);
            });
        }

        this.controls.mute.on('click keydown', (e)=>{
            this.muteVolume(e);
            this.textSwitch(this.controls.mute);
        });

        this.element[0].addEventListener('timeupdate', (e)=>{
            this.timeUpdate(e);
        });

        $(this.progressBar).on("input change",(e)=>{
            this.timeUpdate(e)
        });

        $(this.controls.volume).on("input change",(e)=>{
            this.changeVolume(e);
        });


        // Firefox ist oft schon in State 2, wenn wir hier sind.
        // Und manchmal endet er auf State 1, ohne das loadedmetadata Event auszulösen ...
        if(this.element[0].readyState >= 1) this.loadMeta();
    }

    protected loadMeta() {
        this.container.addClass('loading').removeClass('ready');
        $(this.progressBar).attr('max',this.element[0].duration);

        this.progressText.current.text(this.timeFormat(this.element[0].currentTime, this.element[0].duration));
        this.progressText.remaining.text(this.timeFormat((this.element[0].duration - this.element[0].currentTime),this.element[0].duration));
        this.progressText.duration.text(this.timeFormat(this.element[0].duration,this.element[0].duration));

        let vol=parseFloat(this.controls.volume?.val() as string ?? '100');
        this.setVolume(vol);
        setTimeout(()=>this.buffer(), 150);
        this.container.removeClass('loading').addClass('ready');
    }

    protected buffer() {
        let perc = 0;

        if(!this.element[0].buffered || !this.element[0].buffered.length) return;
        perc = 100 * this.element[0].buffered.end(0) / this.element[0].duration;

        this.progressBar.css('--buffer', Math.round(perc) + "%");
        if (Math.round(perc) < 100) setTimeout(()=>this.buffer(), 500);
    }

    public togglePlay(e:JQuery.TriggeredEvent) {
        super.togglePlay(e);
        if(e.type=="keydown" && e.key != " " && e.key != "Enter") {
            return;
        } else {
            if(e) e.preventDefault();
        }

        if(this.controls) this.controls.play.removeClass('restart').toggleClass('play', this.isPlaying).toggleClass('pause', !this.isPlaying);
    };

    protected toggleCaptions(e:JQuery.TriggeredEvent) {
        if (!this.controls.cc) return;
        let id = e.currentTarget.dataset.index;
        let track = this.element[0].textTracks[id];
        let isActive = !this.controls.cc.button.hasClass('active');
        this.trackID = id;
        this.controls.cc.button.toggleClass('active', isActive);
        this.container.toggleClass('active-caption', isActive);
        if(!isActive) this.container.find('.transcript span.active').removeClass('active');
    }

    protected replay() {
        this.container.removeClass('play pause').addClass('restart');
        if(this.controls) this.controls.play.removeClass('play pause').addClass('restart');
    }

    protected toggleDescription() {
        if (!this.controls.transcript) return;
        let state = !this.controls.transcript.hasClass('active');
        this.container.toggleClass('active-transcript', state);
        this.controls.transcript.toggleClass('active', state);
    }

    private oncuechange() {
        if (!this.controls.cc) return;
        if (!this.controls.cc.button.hasClass('active')) return;
        let cue=(this.element[0].textTracks[0].activeCues?.[0] as VTTCue);
        if (this.ccLine) this.ccLine[0].innerHTML='';
        this.container.find('.cc-container').removeClass('active');
        let transcriptContainer = this.container.find('.transcript');
        transcriptContainer.find('span.active').removeClass('active');

        if (!cue) return;
        let doc=cue.getCueAsHTML();
        let current = transcriptContainer.find('span[data-end="' + cue.endTime + '"]');

        if(current) {
            current.addClass('active');
            let transcriptContainerHeight=transcriptContainer.height()??0;
            let currentHeight=current.height()??0;
            let transcriptTop=current[0].offsetTop - (transcriptContainerHeight / 3) - (currentHeight / 2) ;
            transcriptContainer[0].scrollTo({behavior: "smooth", top: transcriptTop });
        }

        if (this.ccLine) this.ccLine[0].appendChild(doc);
        this.container.find('.cc-container').addClass('active');
    }

    protected timeUpdate(e:any) {
        if(!this.changing && e.type == 'timeupdate') {
            this.progressBar.val(this.element[0].currentTime);
        } else {
            this.changing = e.type == 'input';
            if(this.changing) {
                if(!this.element[0].paused) this.element[0].pause();
            } else {
                if(this.isPlaying && this.element[0].paused) this.element[0].play();
            }
            if (this.progressBar.length) {
                this.element[0].currentTime = parseInt(this.progressBar.val() as string);
            }
            if(this.container.hasClass('restart')) {
                this.container.removeClass('restart').toggleClass('play', this.isPlaying).toggleClass('pause', !this.isPlaying);
                this.controls.play.removeClass('restart').toggleClass('play', this.isPlaying).toggleClass('pause', !this.isPlaying);
            }
        }

        if(typeof this.progressText.current != 'undefined') {
            this.progressText.current.text(this.timeFormat(Math.round(this.element[0].currentTime), Math.round(this.element[0].duration)));
            $(this.progressBar).attr('aria-valuetext',this.humanFormat(this.element[0].currentTime));
        }
        if(typeof this.progressText.remaining != 'undefined') this.progressText.remaining.text(this.timeFormat((this.element[0].duration - this.element[0].currentTime),this.element[0].duration));

        this.progressBar.css('--value', ((this.element[0].currentTime / this.element[0].duration) * 100) + "%");
    }

    protected setVolume(volume:number) {
        if (this.controls.volume) {
            this.controls.volume.css('--value', volume + "%");
            this.controls.volume.attr('aria-valuetext', "Lautstärke " + volume + "%");
        }
        if(this.controls.mute){
            if (volume == 0) {
                this.controls.mute.removeClass('low medium high').addClass('silent');
            } else if(volume <= 30) {
                this.controls.mute.removeClass('silent medium high').addClass('low');
            } else if(volume <= 60) {
                this.controls.mute.removeClass('silent low high').addClass('medium');
            } else {
                this.controls.mute.removeClass('silent low medium').addClass('high');
            }
        }

        this.element[0].volume = volume/100;
    }

    protected changeVolume(e:JQuery.TriggeredEvent) {
        this.setVolume(e.currentTarget.value);
    }

    protected muteVolume(e:JQuery.TriggeredEvent) {
        if(e.type=="keydown" && e.key != " " && e.key != "Enter") {
            return;
        } else {
            e.preventDefault();
        }
        this.controls.mute.toggleClass('muted',this.element[0].muted = !this.element[0].muted);
    }

    private timeFormat(time:number,duration:number) {
        var h = Math.floor(Math.round(time) / 3600);
        time -= h * 3600;
        var m = Math.floor(Math.round(time) / 60);
        time -= m * 60;
        var s = Math.round(time);
        if(h > 0 || duration >= 3600 ) return this.str_pad_left(h) + ":" + this.str_pad_left(m) + ":" + this.str_pad_left(s);
        return this.str_pad_left(m) + ":" + this.str_pad_left(s);
    }

    private humanFormat(time:number) {
        var h = Math.floor(Math.round(time) / 3600);
        time -= h * 3600;
        var m = Math.floor(Math.round(time) / 60);
        time -= m * 60;
        var s = Math.round(time);

        var string = "";
        if(h > 0) {
            string += h + " Stunden";
        }

        if(m > 0) {
            string += m + " Minuten";
        }

        string += s + " Sekunden";

        return string;
    }

    private str_pad_left(string:string|number,pad="0",length=2) {
        return (new Array(length+1).join(pad)+string).slice(-length);
    }
}
export {};